import { useCallback, useRef } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';
import { useGetErrorMessage } from 'js/hooks/server';
import { useCheckUsernameAvailability } from 'server';
import { TValidationMethod } from 'js/utils/Validation/types';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';
import { ICheckUsernameAvailabilityResponseData } from 'js/hooks/socket/hooks/useCheckUsernameAvailability/types';

import { fieldToImsValue } from '../../../../helpers';
import { useWithRecordLevelValidation } from '../useWithRecordLevelValidation';

import * as Types from './types';

export const useValidatePhone = ({
    field,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest
}: Types.IUseValidatePhoneOptions) => {
    const getErrorMessage = useGetErrorMessage();
    const translate = useTranslations();
    const checkIfPhoneIsAvailable = useCheckUsernameAvailability();
    const allowedCountries = useAppSelector(prop('allowedCountries'));
    const cachedPhone = useRef<string | null>(null);
    const abortController = useRef<AbortController | null>(null);
    const cachedRecaptchaValidationRequest = useRef<Promise<{ token: string }> | null>(null);
    const cachedServerResponse = useRef<ICheckUsernameAvailabilityResponseData | null>(null);
    const withRecordLevelValidation = useWithRecordLevelValidation({ fieldName: field.name });

    // this has to be async function so that React Final Form will wait for resolution
    const asyncServerRequest = useCallback(async function process(
        phone: string,
        validationToken?: string
    ): Promise<Types.IUseValidatePhoneResponse> {
        try {
            const response = await checkIfPhoneIsAvailable({
                phone,
                ...validationToken && { token: validationToken }
            });

            const { phoneAvailable } = response;

            cachedPhone.current = phone;
            cachedServerResponse.current = response;
            cachedRecaptchaValidationRequest.current = null;
            abortController.current?.abort();

            if (!phoneAvailable) {
                return field.type === FORM_FIELD_TYPE.PHONE
                    ? translate('playtech.mobile.system.form.phone.internal.phoneIsInUse')
                    : translate('playtech.mobile.system.form.cellphone.internal.cellphoneIsInUse');
            }
        } catch (error: Types.TCheckIfPhoneIsAvailableErrorResponse) {
            const { data: { errorCode } } = error.response;

            if (errorCode === 301) {
                try {
                    abortController.current = new AbortController();
                    abortController.current?.signal.addEventListener('abort', abortReCaptchaTokenRequest);

                    const tokenResponse = requestReCaptchaToken();

                    cachedRecaptchaValidationRequest.current = tokenResponse;

                    const { token } = await tokenResponse;

                    return await process(phone, token);
                } catch (validationError) {
                    log('error', validationError);

                    return getErrorMessage(validationError);
                }
            }

            return getErrorMessage(error);
        }
    }, [
        translate,
        field.type,
        getErrorMessage,
        requestReCaptchaToken,
        checkIfPhoneIsAvailable,
        abortReCaptchaTokenRequest
    ]);

    // this has to be async function so that React Final Form will wait for resolution
    const asyncCachedRequest = useCallback(
        async (phone: string, recaptchaValidationPromise: Promise<{ token: string }>) => {
            let recaptchaToken;

            try {
                const data = await recaptchaValidationPromise;

                recaptchaToken = data.token;
            } catch {
                return await asyncServerRequest(phone);
            }

            return await asyncServerRequest(phone, recaptchaToken);
        }, [asyncServerRequest]);

    const phoneValidate = useCallback<TValidationMethod<string, Record<string, string>>>(async (value, formFields) => {
        if (!value) {
            return;
        }

        const phone = <string>fieldToImsValue(formFields, field, allowedCountries);

        if (cachedPhone.current === phone) {
            const { phoneAvailable } = cachedServerResponse.current ?? {};

            if (phoneAvailable) {
                return null;
            }

            return field.type === FORM_FIELD_TYPE.PHONE
                ? translate('playtech.mobile.system.form.phone.internal.phoneIsInUse')
                : translate('playtech.mobile.system.form.cellphone.internal.cellphoneIsInUse');
        }

        if (cachedRecaptchaValidationRequest.current) {
            return asyncCachedRequest(phone, cachedRecaptchaValidationRequest.current);
        }

        return asyncServerRequest(phone);
    }, [
        field,
        translate,
        allowedCountries,
        asyncServerRequest,
        asyncCachedRequest
    ]);

    return withRecordLevelValidation(phoneValidate);
};
